import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Banknote, CheckCircle2, Info } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { formatRupiah, formatDate } from "@/lib/dummyData";
import { useAuth } from "@/lib/auth";
import { SavingsAPI } from "@/lib/api";

const STATUS_STYLE = {
  PENDING: "bg-amber-warm/20 text-amber-700",
  APPROVED: "bg-sky-100 text-sky-700",
  PAID: "bg-sage/20 text-forest",
  REJECTED: "bg-terracotta/15 text-terracotta",
};
const STATUS_LABEL = {
  PENDING: "Menunggu Verifikasi",
  APPROVED: "Terverifikasi — Menunggu Transfer",
  PAID: "Selesai (Sudah Ditransfer)",
  REJECTED: "Ditolak",
};

export default function SavingsWithdraw() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [sukarela, setSukarela] = useState(0);
  const [rows, setRows] = useState([]);
  const [amount, setAmount] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    SavingsAPI.mine().then((d) => setSukarela(Number(d.savings?.sukarela || 0))).catch(() => {});
    SavingsAPI.myWithdrawals().then(setRows).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (user?.name) setAccountHolder((v) => v || user.name); }, [user]);

  // Dana yang masih tertahan pengajuan berjalan (PENDING/APPROVED) mengurangi saldo yang bisa ditarik.
  const onHold = rows.filter((r) => ["PENDING", "APPROVED"].includes(r.status)).reduce((a, r) => a + Number(r.amount), 0);
  const tersedia = Math.max(0, sukarela - onHold);

  const submit = async () => {
    const nominal = Number(amount);
    if (!nominal || nominal <= 0) return toast.error("Isi nominal penarikan yang benar");
    if (nominal > tersedia) return toast.error(`Maksimal penarikan ${formatRupiah(tersedia)}`);
    if (!bankName || !accountNumber || !accountHolder) return toast.error("Lengkapi data rekening tujuan");
    setSending(true);
    try {
      await SavingsAPI.requestWithdrawal({
        amount: nominal,
        bank_name: bankName,
        bank_account_number: accountNumber,
        account_holder: accountHolder,
      });
      toast.success("Pengajuan penarikan terkirim — menunggu verifikasi Admin Keuangan.");
      setAmount("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal mengajukan penarikan");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <Link to="/anggota/tabungan" className="inline-flex items-center gap-1.5 text-sm text-forest hover:underline">
          <ArrowLeft size={15} /> Kembali ke Buku Tabungan
        </Link>
        <h1 className="font-display text-3xl font-bold text-ink tracking-tight mt-3">Tarik Simpanan Sukarela</h1>
        <p className="text-ink/75 mt-2 text-sm">
          Pengajuan penarikan diverifikasi lalu dikonfirmasi oleh Admin Keuangan sebelum dana ditransfer ke rekening Anda.
        </p>
      </div>

      {/* Ringkasan saldo */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="rounded-2xl border-stone-soft p-5">
          <div className="text-[10px] uppercase tracking-wider text-ink/55 font-semibold">Saldo Sukarela</div>
          <div className="font-display font-bold text-xl text-ink mt-1">{formatRupiah(sukarela)}</div>
        </Card>
        <Card className="rounded-2xl border-stone-soft p-5">
          <div className="text-[10px] uppercase tracking-wider text-ink/55 font-semibold">Dalam Pengajuan</div>
          <div className="font-display font-bold text-xl text-amber-700 mt-1">{formatRupiah(onHold)}</div>
        </Card>
        <Card className="rounded-2xl border-stone-soft p-5">
          <div className="text-[10px] uppercase tracking-wider text-ink/55 font-semibold">Bisa Ditarik</div>
          <div className="font-display font-bold text-xl text-forest mt-1">{formatRupiah(tersedia)}</div>
        </Card>
      </div>

      {/* Alur penarikan */}
      <Card className="rounded-3xl border-stone-soft p-6">
        <div className="flex items-start gap-2 text-sm text-ink/80">
          <Info size={15} className="text-forest mt-0.5 shrink-0" />
          <p>
            Alur penarikan: <b>Diajukan</b> → <b>Diverifikasi</b> Admin Keuangan → <b>Dikonfirmasi & Ditransfer</b> ke
            rekening Anda (saldo sukarela terpotong saat konfirmasi). Pengajuan dapat ditolak bila data rekening tidak
            valid atau saldo tidak mencukupi.
          </p>
        </div>
      </Card>

      {/* Form pengajuan */}
      <Card className="rounded-3xl border-stone-soft p-6">
        <h3 className="font-display font-semibold text-ink mb-4">Form Pengajuan Penarikan</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Nominal Penarikan</Label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Maks. ${tersedia}`} className="bg-cream border-stone-soft rounded-lg" data-testid="withdraw-amount" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Bank Tujuan</Label>
            <Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="cth. BCA" className="bg-cream border-stone-soft rounded-lg" data-testid="withdraw-bank" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Nomor Rekening</Label>
            <Input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} className="bg-cream border-stone-soft rounded-lg" data-testid="withdraw-account" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Nama Pemilik Rekening</Label>
            <Input value={accountHolder} onChange={(e) => setAccountHolder(e.target.value)} className="bg-cream border-stone-soft rounded-lg" data-testid="withdraw-holder" />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <Button variant="outline" className="rounded-full flex-1" onClick={() => navigate("/anggota/tabungan")}>Batal</Button>
          <Button disabled={sending} className="rounded-full flex-1 bg-forest hover:bg-forest-dark text-white" onClick={submit} data-testid="withdraw-submit">
            <CheckCircle2 size={16} /> {sending ? "Mengirim…" : "Ajukan Penarikan"}
          </Button>
        </div>
      </Card>

      {/* Riwayat pengajuan */}
      <Card className="rounded-3xl border-stone-soft p-6">
        <h3 className="font-display font-semibold text-ink mb-4 flex items-center gap-2"><Banknote size={16} className="text-forest" /> Riwayat Pengajuan Penarikan</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-stone-soft hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-wider text-ink/55">Tanggal</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-ink/55 text-right">Nominal</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-ink/55">Rekening Tujuan</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-ink/55">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-ink/55">Catatan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-ink/55 py-8">Belum ada pengajuan penarikan.</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} className="border-stone-soft hover:bg-cream/50">
                  <TableCell className="text-sm">{formatDate(r.created_at)}</TableCell>
                  <TableCell className="text-right text-sm font-semibold text-ink">{formatRupiah(r.amount)}</TableCell>
                  <TableCell className="text-sm text-ink/75">{r.bank_name} {r.bank_account_number}<div className="text-[11px] text-ink/55">a.n. {r.account_holder}</div></TableCell>
                  <TableCell><Badge className={`${STATUS_STYLE[r.status]} border-0 rounded-full text-[10px] uppercase tracking-wider`}>{STATUS_LABEL[r.status] || r.status}</Badge></TableCell>
                  <TableCell className="text-xs text-ink/55">{r.note || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
